import {Box, Chip, FormControlLabel, Grid, Switch, Typography} from "@mui/material";
import MySelect from "../../../../components/MySelect";
import MyTextField from "../../../../components/MyTextField";
import BilltoSearch from "../../../../components/BilltoSearch";
import RetailLocationSearch from "../../../../components/RetailLocationSearch";
import ShiptoSearch from "../../../../components/ShiptoSearch";
import {useSalesRegions} from "../../../../hooks/useSalesRegions";
import MultiTerritoryPicker from "../../MultiTerritoryPicker";
import FacilityPicker from "../../FacilityPicker";

const sectionLabel = {fontWeight: 700, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 0.5};

// Step 4 — Additional Details. Every field is conditional on the user type (see userTypeConfig).
const Step4Details = (ctx) => {
    const {draft, setField, cfg, grantsAdmin, businessUnitCode, currentUser} = ctx;

    const adminOn = grantsAdmin ? true : !!draft.adminPortalAccess;

    // Ship-to is scoped by business unit, and by the selected Bill-to for user types that have one.
    // Remounting on either clears the picker's own selection when the wizard clears the draft value.
    const shiptoField = (
        <ShiptoSearch
            key={`${businessUnitCode}|${draft.billtoId}`}
            label="Ship-to"
            businessUnitCode={businessUnitCode}
            billtoId={cfg.billto ? draft.billtoId : undefined}
            billtoRequired={!!cfg.billto}
            editable={true}
            required={!!cfg.shiptoRequired}
            initialValue={draft.shiptoId}
            onChange={(value) => setField("shiptoId", value?.shiptoId || "")}
        />
    );

    return (
        <Box sx={{maxWidth: 720, mx: "auto"}}>
            <Grid container spacing={2}>
                {cfg.orderPin && (
                    <Grid item xs={12}>
                        <MyTextField
                            value={draft.orderPin}
                            editable={true}
                            label="Order Pin"
                            type="text"
                            required={false}
                            onChange={(e) => setField("orderPin", e.target.value)}
                        />
                    </Grid>
                )}

                {cfg.shipto && !cfg.billto && (
                    <Grid item xs={12}>
                        {shiptoField}
                    </Grid>
                )}

                {cfg.billto && (
                    <Grid item xs={12}>
                        <BilltoSearch
                            label="Bill-to"
                            businessUnitCode={businessUnitCode}
                            editable={true}
                            required={true}
                            initialValue={draft.billtoId}
                            onChange={(value) => setField("billtoId", value?.billtoId || "")}
                        />
                    </Grid>
                )}

                {cfg.retailLoc && (
                    <Grid item xs={12}>
                        <RetailLocationSearch
                            label="Retail Location"
                            businessUnitCode={businessUnitCode}
                            billtoId={draft.billtoId}
                            editable={true}
                            required={true}
                            initialValue={draft.retailLocationId}
                            onChange={(value) => setField("retailLocationId", value?.id || "")}
                        />
                    </Grid>
                )}

                {cfg.shipto && cfg.billto && (
                    <Grid item xs={12}>
                        {shiptoField}
                    </Grid>
                )}
            </Grid>

            {cfg.hasSalesRegion && (
                <Box sx={{mt: 2}}>
                    <Typography sx={sectionLabel}>Sales Region</Typography>
                    <SalesRegionSelect
                        businessUnitCode={businessUnitCode}
                        userId={currentUser?.id}
                        value={draft.salesRegionId}
                        onChange={(id) => setField("salesRegionId", id)}
                    />
                </Box>
            )}

            {cfg.hasSalesTerritories && (
                <MultiTerritoryPicker
                    businessUnitCode={businessUnitCode}
                    userId={currentUser?.id}
                    value={draft.salesTerritories}
                    onChange={(list) => setField("salesTerritories", list)}
                />
            )}

            {cfg.hasFacilities && (
                <FacilityPicker
                    businessUnitCode={businessUnitCode}
                    value={draft.facilities}
                    onChange={(list) => setField("facilities", list)}
                />
            )}

            {cfg.adminAccess && (
                <Box sx={{mt: 3}}>
                    <Typography sx={sectionLabel}>Admin Portal Access</Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={adminOn}
                                disabled={grantsAdmin}
                                onChange={(e) => setField("adminPortalAccess", e.target.checked)}
                            />
                        }
                        label={adminOn ? "Can access the Admin Portal" : "No Admin Portal access"}
                    />
                    {grantsAdmin && (
                        <Chip size="small" label="Granted by group" color="info" variant="outlined" sx={{ml: 1}} />
                    )}
                </Box>
            )}
        </Box>
    );
};

// Single Sales Region selection (region id is the committed value).
const SalesRegionSelect = ({businessUnitCode, userId, value, onChange}) => {
    const regions = useSalesRegions(businessUnitCode, userId, !!businessUnitCode);
    return (
        <MySelect
            label="Sales Region"
            options={regions}
            value={value}
            required={false}
            onChange={onChange}
        />
    );
};

export default Step4Details;
